import React, { Suspense, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { AlertTriangle, Camera, CameraOff, CheckCircle2, Keyboard, Loader2, Plus, RotateCcw, XCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { inputClass } from '../ui/Field';
import { READ_EFFECT_MS, READ_ERROR_MS } from './scannerConfig';
import { cameraErrorCopy } from './scannerUtils';

const CameraUnavailable = () => (
  <div className="flex h-full items-center justify-center p-4 text-center text-sm text-white/80">
    No pudimos cargar la cámara. Recarga la página, o escribe el código abajo.
  </div>
);

// A failed download of the camera's chunk falls back to typing, with no error boundary needed.
const CameraScanner = React.lazy(() =>
  import('./CameraScanner').catch(() => ({ default: CameraUnavailable }))
);

/** A verdict on the guide: its border, its badge and its icon, by tone. */
const VERDICT_LOOKS = {
  success: { border: 'border-ninja-green', badge: 'bg-ninja-green text-white', Icon: CheckCircle2 },
  step: { border: 'border-ninja-green', badge: 'bg-ninja-green text-white', Icon: CheckCircle2 },
  error: { border: 'border-danger', badge: 'bg-danger text-white', Icon: XCircle },
  warning: { border: 'border-warning', badge: 'bg-warning text-ninja-dark', Icon: AlertTriangle },
};

/**
 * How long a camera read handed over waits for its verdict while the screen
 * still takes reads: a local check answers at once, and a request makes the
 * screen busy. Only a read the gate turned away waits it out.
 */
const VERDICT_WAIT_MS = 300;

/**
 * A read's verdict over the guide, the size of it, until it clears. The same
 * label failing again in a row says so: reading it again will not change it.
 */
const ReadVerdict = ({ code, tone, failures = 0 }) => {
  const { border, badge, Icon } = VERDICT_LOOKS[tone] || VERDICT_LOOKS.success;
  return (
    <div
      data-testid="scan-read-effect"
      data-tone={tone}
      data-failures={failures || undefined}
      className={clsx(
        'pointer-events-none absolute inset-x-6 top-1/2 flex min-h-24 -translate-y-1/2 flex-col items-center justify-center gap-1.5 rounded-xl border-4 px-2 py-2',
        'animate-in fade-in zoom-in-95 duration-200 motion-reduce:animate-none',
        border
      )}
    >
      <span className={clsx('flex max-w-full items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold shadow', badge)}>
        <Icon size={18} className="shrink-0" />
        <span className="break-all">{code}</span>
        {failures > 1 && <span className="shrink-0">×{failures}</span>}
      </span>
      {failures > 1 && (
        <span data-testid="scan-read-again" className="rounded-md bg-black/60 px-2 py-1 text-center text-xs font-semibold text-white">
          Es la misma etiqueta: seguirá fallando.
        </span>
      )}
    </div>
  );
};

/**
 * Where a code comes in: the camera, or typed. Typing is always there — a
 * denied camera, a damaged label, a desktop without one — and it is also how
 * a Bluetooth or USB reader that types its codes works.
 *
 * Presentational: it says what was read and how, and the store decides what
 * it means. While `accepting` is false nothing goes through: a read goes to
 * `onHold` when the screen holds reads; otherwise the camera offers it again
 * once the screen takes reads, if it is still in view, and a typed one is
 * dropped (`onDrop`). Either way the field empties at once, so a reader's next
 * code is never glued to the last one.
 *
 * While a read is processed the whole camera is blurred (`scan-processing`),
 * with «Leyendo…» or the busy label, and then the verdict of a camera read on
 * the guide for `READ_EFFECT_MS`. The camera hands nothing over meanwhile:
 * once it clears, it reads again.
 *
 * @param {object} props
 * @param {string} props.prompt - What to scan now, large.
 * @param {string} [props.hint]
 * @param {boolean} props.accepting
 * @param {string|null} [props.lastCode] - The last code the store accepted; the camera prefers any other.
 * @param {(code: string, alternatives: string[], read?: {again: boolean, source?: string}) => void} [props.onHold] -
 *   A read while not accepting; `read.source` is `'typed'` for a typed one.
 * @param {(code: string) => void} [props.onDrop] - A typed read while busy that nothing holds.
 * @param {{code: string}|null} [props.dropped] - A read that was not read: the panel says so.
 * @param {string} [props.busyLabel='Registrando…']
 * @param {boolean} props.cameraOn
 * @param {boolean} [props.cameraPaused]
 * @param {{kind: string, message: string}|null} [props.cameraError]
 * @param {number} [props.cameraEpoch] - Changing it remounts the camera.
 * @param {{codes: string[], id: string}|null} [props.turnedAway] - The last camera read the store rejected.
 * @param {string|null} [props.rereadOn] - The screen's step: moving on offers again what the camera sees.
 * @param {string} [props.tone] - The last outcome's tone: a camera read's verdict, on the guide and the frame.
 * @param {string|null} [props.verdictId] - The last outcome's id: a new one is the verdict of the read handed over.
 * @param {string} [props.repeatLabel] - e.g. "+1 POL-001"; shows the repeat button.
 * @param {() => void} [props.onRepeat]
 * @param {(code: string, source: 'camera'|'typed', alternatives?: string[], read?: {again: boolean}) => void} props.onCode
 *   `alternatives`: the other codes the camera saw with this one; `read.again`: the code came back into view.
 * @param {(on: boolean) => void} props.onCameraToggle
 * @param {(error: {kind: string, message: string}) => void} props.onCameraError
 * @param {(reason: string) => void} [props.onCameraStall] - The camera stopped without an error.
 * @param {() => void} props.onCameraRetry
 * @param {boolean} [props.cameraDiagnostics] - Shows what the camera sees and does, over its image.
 * @param {string} [props.testId='scan-panel']
 */
export const ScanPanel = ({
  prompt,
  hint,
  accepting,
  lastCode = null,
  onHold,
  onDrop,
  dropped = null,
  busyLabel = 'Registrando…',
  cameraOn,
  cameraPaused = false,
  cameraError = null,
  cameraEpoch = 0,
  turnedAway = null,
  rereadOn = null,
  tone,
  verdictId = null,
  repeatLabel,
  onRepeat,
  onCode,
  onCameraToggle,
  onCameraError,
  onCameraStall,
  onCameraRetry,
  cameraDiagnostics = false,
  testId = 'scan-panel',
}) => {
  const [typed, setTyped] = useState('');
  // The camera read handed over, until its verdict; then the verdict, until it clears.
  const [handed, setHanded] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const lastVerdictId = useRef(verdictId);
  // The last failed camera read, `{code, failures}`, while the same label keeps failing in a row.
  const lastFailure = useRef(null);

  useEffect(() => {
    if (verdictId === lastVerdictId.current) return;
    lastVerdictId.current = verdictId;
    if (!handed || !verdictId) return;
    const failed = tone === 'error';
    const failures = failed && lastFailure.current?.code === handed ? lastFailure.current.failures + 1 : 1;
    lastFailure.current = failed ? { code: handed, failures } : null;
    setVerdict({ code: handed, tone, failures: failed ? failures : 0 });
    setHanded(null);
  }, [verdictId, handed, tone]);

  // A read the gate turned away says nothing: its wait ends while the screen takes reads.
  useEffect(() => {
    if (!handed || !accepting) return undefined;
    const timer = setTimeout(() => setHanded(null), VERDICT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [handed, accepting]);

  useEffect(() => {
    if (!verdict) return undefined;
    const timer = setTimeout(() => setVerdict(null), verdict.tone === 'error' ? READ_ERROR_MS : READ_EFFECT_MS);
    return () => clearTimeout(timer);
  }, [verdict]);

  const readFromCamera = (code, alternatives, read) => {
    if (verdict) return false;
    if (accepting) {
      // The code the store took last, back without leaving the view, is turned away: nothing to wait for.
      if (code !== lastCode || read?.again) setHanded(code);
      onCode(code, 'camera', alternatives, read);
      return true;
    }
    if (onHold) {
      onHold(code, alternatives, read);
      return true;
    }
    return false;
  };

  const takeTyped = () => {
    const code = typed.trim();
    setTyped('');
    if (!code) return;
    if (accepting) onCode(code, 'typed');
    else if (onHold) onHold(code, [], { again: false, source: 'typed' });
    else onDrop?.(code);
  };

  const submitTyped = (event) => {
    event.preventDefault();
    takeTyped();
  };

  // With «Usar» off, Enter submits nothing; a reader's Enter still has to empty the field.
  const enterWhileBusy = (event) => {
    if (event.key !== 'Enter' || accepting) return;
    event.preventDefault();
    takeTyped();
  };

  const errorCopy = cameraError ? cameraErrorCopy(cameraError.kind) : null;
  // The frame says whether the camera reads now: red while it does not, the verdict's color while it shows.
  const cameraReady = accepting && !handed && !verdict;
  let frameBorder = 'border-transparent';
  if (verdict) frameBorder = (VERDICT_LOOKS[verdict.tone] || VERDICT_LOOKS.success).border;
  else if (!cameraReady) frameBorder = 'border-danger';

  return (
    <section
      data-testid={testId}
      className="rounded-3xl border border-border bg-white p-4 shadow-sm dark:bg-[#1a1a1a] space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p data-testid="scan-prompt" className="text-lg font-bold leading-snug text-ninja-dark dark:text-white">
            {prompt}
          </p>
          {hint && (
            <p data-testid="scan-hint" className="mt-1 text-sm text-text-secondary">
              {hint}
            </p>
          )}
          {dropped && (
            <p data-testid="scan-dropped" className="mt-1 break-all text-sm font-semibold text-warning">
              No se leyó {dropped.code}: vuelve a leerla.
            </p>
          )}
        </div>
        {cameraOn && (
          <Button
            variant="subtle"
            size="lg"
            iconOnly
            icon={CameraOff}
            aria-label="Apagar la cámara"
            onClick={() => onCameraToggle(false)}
            data-testid="scan-camera-disable"
          />
        )}
      </div>

      {cameraOn && !cameraError && (
        <div
          data-testid="scan-camera"
          data-paused={String(cameraPaused)}
          data-ready={String(cameraReady)}
          className={clsx(
            'relative h-56 overflow-hidden rounded-2xl border-4 bg-black transition-colors',
            frameBorder
          )}
        >
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center gap-2 text-sm text-white/80">
                <Loader2 size={18} className="animate-spin" /> Abriendo la cámara…
              </div>
            }
          >
            <CameraScanner
              key={cameraEpoch}
              paused={cameraPaused}
              skipCode={lastCode}
              turnedAway={turnedAway}
              rereadOn={rereadOn}
              diagnostics={cameraDiagnostics}
              onCode={readFromCamera}
              onError={onCameraError}
              onStall={onCameraStall}
            />
          </Suspense>
          {(verdict || handed || !accepting) && (
            <div
              data-testid="scan-processing"
              data-state={verdict ? 'verdict' : 'reading'}
              className="absolute inset-0 bg-black/40 backdrop-blur-md animate-in fade-in duration-150 motion-reduce:animate-none"
            >
              {verdict ? (
                <ReadVerdict code={verdict.code} tone={verdict.tone} failures={verdict.failures} />
              ) : (
                <div
                  data-testid={accepting ? 'scan-reading' : 'scan-busy'}
                  className="flex h-full flex-col items-center justify-center gap-1 px-4 text-center text-sm font-semibold text-white"
                >
                  <span className="flex items-center gap-2">
                    <Loader2 size={18} className="animate-spin" /> {accepting ? 'Leyendo…' : busyLabel}
                  </span>
                  {handed && (
                    <span data-testid="scan-busy-code" className="break-all font-mono text-xs text-white/80">
                      {handed}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {cameraOn && cameraError && (
        <div
          data-testid="scan-camera-error"
          data-kind={cameraError.kind}
          className="space-y-3 rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm dark:border-orange-900 dark:bg-orange-900/20"
        >
          <p className="font-semibold text-ninja-dark dark:text-white">{errorCopy.title}</p>
          <p className="text-text-secondary">{errorCopy.hint}</p>
          {errorCopy.canRetry && (
            <Button variant="outline" size="lg" icon={RotateCcw} onClick={onCameraRetry} data-testid="scan-camera-retry">
              Reintentar
            </Button>
          )}
        </div>
      )}

      {!cameraOn && (
        <Button
          variant="outline"
          size="lg"
          fullWidth
          icon={Camera}
          onClick={() => onCameraToggle(true)}
          data-testid="scan-camera-enable"
        >
          Usar la cámara
        </Button>
      )}

      <form onSubmit={submitTyped} className="flex gap-2">
        <label htmlFor={`${testId}-manual`} className="sr-only">
          Escribir el código
        </label>
        <div className="relative flex-1">
          <Keyboard size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            id={`${testId}-manual`}
            data-testid="scan-manual-input"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            onKeyDown={enterWhileBusy}
            placeholder="Escribir el código"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="go"
            className={clsx(inputClass, 'h-12 pl-10 text-base')}
          />
        </div>
        <Button
          type="submit"
          variant="secondary"
          size="lg"
          disabled={!accepting || !typed.trim()}
          data-testid="scan-manual-submit"
        >
          Usar
        </Button>
      </form>

      {repeatLabel && (
        <Button
          variant="outline"
          size="lg"
          fullWidth
          icon={Plus}
          disabled={!accepting}
          onClick={onRepeat}
          data-testid="scan-repeat"
        >
          {repeatLabel}
        </Button>
      )}
    </section>
  );
};
